import './trainerRedaction.scss';

import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { CloseSVGIcon, RedactionSVGIcon } from '../svg';
import { TrainerRedactionState } from './trainerRedactionState';
import { RedactionResizeHandle, VisualRedactionEffect, VisualRedactionShape } from './trainerRedactionUtils';

type Props = {
  model: TrainerRedactionState;
};

const effects: VisualRedactionEffect[] = ['Blur', 'Pixelate', 'Blackout'];
const shapes: VisualRedactionShape[] = ['Rectangle', 'Ellipse'];
const resizeHandles: RedactionResizeHandle[] = ['nw', 'ne', 'sw', 'se'];

const pointFromEvent = (evt: React.PointerEvent<HTMLElement>) => {
  const canvas = evt.currentTarget.closest('.redaction-canvas');
  const rect = canvas?.getBoundingClientRect() ?? evt.currentTarget.getBoundingClientRect();
  return {
    x: (evt.clientX - rect.left) / rect.width,
    y: (evt.clientY - rect.top) / rect.height,
  };
};

export const TrainerRedaction = observer(({ model }: Props) => {
  if (!model.isVisualMode && model.activeVisualRedactions.length === 0)
    return null;

  const draft = model.draftRegion;

  return (
    <div className={classNames('trainer-redaction-layer', { active: model.isVisualMode })}>
      {model.isVisualMode && (
        <div className="redaction-mode-banner">
          <RedactionSVGIcon />
          <span>Draw a new box or click an existing box to edit it</span>
          <button type="button" aria-label="Exit redaction mode" onClick={model.exitVisualMode}>
            <CloseSVGIcon />
          </button>
        </div>
      )}

      <div
        className="redaction-canvas"
        onPointerDown={evt => {
          if (evt.target !== evt.currentTarget)
            return;
          evt.currentTarget.setPointerCapture(evt.pointerId);
          model.beginDrawing(pointFromEvent(evt));
        }}
        onPointerMove={evt => model.updateDrawing(pointFromEvent(evt))}
        onPointerUp={evt => model.finishDrawing(pointFromEvent(evt))}>

        {model.activeVisualRedactions
          .filter(({ moment }) => moment.id !== model.editingMomentId)
          .map(({ moment, stored }) => (
          <div
            key={moment.id}
            role={model.isVisualMode ? 'button' : undefined}
            tabIndex={model.isVisualMode ? 0 : undefined}
            className={classNames(
              'redaction-region',
              `effect-${stored.effect.toLowerCase()}`,
              {
                editable: model.isVisualMode,
                ellipse: stored.shape === 'Ellipse',
              },
            )}
            style={{
              left: `${stored.region.x * 100}%`,
              top: `${stored.region.y * 100}%`,
              width: `${stored.region.width * 100}%`,
              height: `${stored.region.height * 100}%`,
            }}
            title={model.isVisualMode ? 'Edit this redaction' : undefined}
            onPointerDown={evt => evt.stopPropagation()}
            onClick={() => model.editVisualRedaction(moment, false)}
            onKeyDown={evt => {
              if (evt.key === 'Enter' || evt.key === ' ')
                model.editVisualRedaction(moment, false);
            }}>
            <span className="redaction-region-label">{stored.effect}</span>
          </div>
          ))}

        {draft && (
          <div
            className={classNames(
              'redaction-region',
              'draft',
              `effect-${model.visualEffect.toLowerCase()}`,
              { ellipse: model.visualShape === 'Ellipse' },
            )}
            style={{
              left: `${draft.x * 100}%`,
              top: `${draft.y * 100}%`,
              width: `${draft.width * 100}%`,
              height: `${draft.height * 100}%`,
            }}
            onPointerDown={evt => {
              if (evt.target !== evt.currentTarget)
                return;
              evt.stopPropagation();
              evt.currentTarget.setPointerCapture(evt.pointerId);
              model.beginRegionMove(pointFromEvent(evt));
            }}
            onPointerMove={evt => model.updateRegionTransform(pointFromEvent(evt))}
            onPointerUp={() => model.finishRegionTransform()}>
            {resizeHandles.map(handle => (
              <span
                key={handle}
                role="button"
                aria-label={`Resize redaction from ${handle} corner`}
                className={`redaction-resize-corner ${handle}`}
                onPointerDown={evt => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  evt.currentTarget.setPointerCapture(evt.pointerId);
                  model.beginRegionResize(pointFromEvent(evt), handle);
                }} />
            ))}

            {!model.isDrawing && (
              <div
                className={classNames('redaction-context-menu', {
                  below: draft.y < 0.25,
                  'align-right': draft.x > 0.55,
                })}
                onPointerDown={evt => evt.stopPropagation()}>
                <strong className="redaction-context-title">
                  {model.editingVisualMoment ? 'Edit redaction' : 'New redaction'}
                </strong>

                <div className="redaction-option-group" role="group" aria-label="Redaction effect">
                  {effects.map(effect => (
                    <button
                      type="button"
                      key={effect}
                      aria-pressed={model.visualEffect === effect}
                      onClick={() => model.setVisualEffect(effect)}>
                      {effect}
                    </button>
                  ))}
                </div>

                <div className="redaction-option-group" role="group" aria-label="Selection shape">
                  {shapes.map(shape => (
                    <button
                      type="button"
                      key={shape}
                      aria-pressed={model.visualShape === shape}
                      onClick={() => model.setVisualShape(shape)}>
                      {shape}
                    </button>
                  ))}
                </div>

                <label className="redaction-time-input">
                  <span>Start</span>
                  <input
                    type="number"
                    min="0"
                    max={model.player.duration}
                    step="0.1"
                    value={Number(model.draftStartTime.toFixed(2))}
                    onChange={evt => model.setDraftStartTime(evt.currentTarget.valueAsNumber)} />
                  <span>s</span>
                </label>

                <label className="redaction-time-input">
                  <span>End</span>
                  <input
                    type="number"
                    min="0.1"
                    max={model.player.duration}
                    step="0.1"
                    value={Number(model.draftEndTime.toFixed(2))}
                    onChange={evt => model.setDraftEndTime(evt.currentTarget.valueAsNumber)} />
                  <span>s</span>
                </label>

                <label className="redaction-time-input">
                  <span>Duration</span>
                  <input
                    type="number"
                    min="0.1"
                    max={model.player.duration}
                    step="0.1"
                    value={Number(model.durationSeconds.toFixed(2))}
                    onChange={evt => model.setDurationSeconds(evt.currentTarget.valueAsNumber)} />
                  <span>s</span>
                </label>

                <p className="redaction-tracking-note">
                  Moving subject? Seek forward, move this box, then choose New segment here. Automatic tracking is not available in this preview.
                </p>

                <div className="redaction-context-actions">
                  {model.editingVisualMoment && (
                    <>
                      <button
                        type="button"
                        className="delete"
                        disabled={model.isSaving}
                        onClick={() => model.deleteEditingVisualRedaction()}>
                        Delete
                      </button>
                      <button
                        type="button"
                        disabled={model.isSaving}
                        onClick={model.prepareFollowUpAtPlayhead}>
                        New segment here
                      </button>
                    </>
                  )}
                  <button type="button" onClick={model.cancelDraft}>Cancel</button>
                  <button
                    type="button"
                    className="save"
                    disabled={model.isSaving}
                    onClick={() => model.saveVisualRedaction()}>
                    {model.editingVisualMoment ? 'Update redaction' : 'Add redaction'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
